/* eslint-disable prettier/prettier */
(function (algolia) {
  'use strict';

  if (!algolia.config.autocomplete_enabled) return;

  const autocomplete = algolia.externals.autocomplete;

  algolia.searchClient.addAlgoliaAgent('Shopify Integration');

  // Retrieve the container from Shopify's config
  const container = document.querySelector(algolia.config.input_selector);
  container.innerHTML = '';
  const containerClasses = container.classList;
  containerClasses.add('aa-MainContainer');

  // Set plugins
  const {
    productsPlugin,
    collectionsPlugin,
    articlesPlugin,
    pagesPlugin,
    querySuggestionsPlugin,
    algoliaInsightsPlugin,
  } = algolia;
  const plugins = [
    algolia.config.analytics_enabled && algoliaInsightsPlugin,
    algolia.config.index_products && productsPlugin,
    algolia.config.index_collections && collectionsPlugin,
    algolia.config.index_articles && articlesPlugin,
    algolia.config.index_pages && pagesPlugin,
    algolia.config.autocomplete_query_suggestions && querySuggestionsPlugin,
  ];

  const cssFile = document.getElementById('template_algolia_autocomplete.css');
  //Set CSS variables
  document.documentElement.style.setProperty(
    `--aa-text-color-rgb`,
    `${algolia.helpers.hexToRGB(algolia.config.colors.secondary)}`
  );
  document.documentElement.style.setProperty(
    `--aa-primary-color-rgb`,
    `${algolia.helpers.hexToRGB(algolia.config.colors.main)}`
  );
  document.documentElement.style.setProperty(
    `--aa-muted-color-rgb`,
    `${algolia.helpers.hexToRGB(algolia.config.colors.muted)}`
  );
  document.documentElement.style.setProperty(
    `--aa-highlight-color-rgb`,
    `${algolia.helpers.hexToRGB(algolia.config.colors.highlight)}`
  );
  //Link CSS
  algolia.appendStyle(cssFile.text);

  // Live filter (KB 922): strip the auto-generated ARIA autocomplete pattern and
  // announce result counts so the popup can hold interactive/focusable content.
  const t = algolia.translations || {};
  const makeLiveFilter =
    window.createLiveFilter ||
    (() => ({ apply() {}, observe() {} }));
  const liveFilter = makeLiveFilter({
    container,
    comboboxSelector: '.aa-Autocomplete',
    inputSelector: '.aa-Input',
    inputLabel: t.searchForProduct || 'Search',
    instructions: t.liveFilterInstructions,
    extraScopeSelector: '.aa-DetachedContainer',
    svgLabels: [
      { selector: '.aa-SubmitButton svg', label: t.searchButtonLabel || 'Search' },
    ],
    messages: {
      none: t.noResultFound,
      one: t.oneResultFound,
      many: `{count} ${t.resultsFound || 'results found'}`,
    },
    liveRegionId: 'algolia-live-filter-status',
  });

  const countResults = (state) =>
    (state.collections || []).reduce(
      (total, c) => total + ((c.items && c.items.length) || 0),
      0
    );

  const addInfoNoResult = () => {
    const noResultContent = document.querySelector('.aa-NoResultInfo');
    if (noResultContent) {
      const elVueNoResult = ".aa-Item-No-Result__vue:not([data-vue-root='ProductAlgolia'][data-v-app])";
      const elDataVueRoot = document.querySelectorAll(elVueNoResult);
      if (elDataVueRoot.length) {
        setTimeout(() => {
          window.renderVueApps(elVueNoResult);
        }, 500);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Accessibility layer — LIVE FILTER pattern.
  // Per the accessibility audit re-scope, search results are exposed as a
  // labelled, navigable region (NOT an autocomplete/combobox listbox) so that
  // screen reader and keyboard users can reach the controls inside each product
  // card (Wishlist, Quick View, rating, product link). This strips the
  // combobox/listbox semantics the library applies and announces the result
  // count. Re-applied on every render because the library rebuilds the markup.
  // Covers AM-16256 (accessible name), AM-16255 (result count) and AM-16339
  // (keyboard access to the results); the autocomplete-specific findings
  // AM-16254/16252/16253/16251/16250 are handled as N/A (no listbox pattern).
  // ---------------------------------------------------------------------------
  algolia.a11y = {
    liveId: 'aa-a11y-live',
    seq: 0,
    announceTimer: null,
    keysBound: false,

    // AM-16255: polite live region used to announce the number of results.
    ensureSrNodes() {
      if (!document.getElementById(this.liveId)) {
        const live = document.createElement('div');
        live.id = this.liveId;
        live.className = 'visually-hidden';
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('aria-atomic', 'true');
        document.body.appendChild(live);
      }
    },

    getInput() {
      return container.querySelector('.aa-Input');
    },

    // AM-16257: host the results panel INSIDE the header, right after the
    // search form (which sits directly below the "Search" button), so screen
    // reader and keyboard users meet the results immediately after the input.
    // The header is a positioned context, so the panel is re-anchored with
    // fixed positioning in positionPanel() — the library's page-coordinate
    // positioning would otherwise place it incorrectly here.
    getPanelHost() {
      let host = document.getElementById('aa-panel-host');
      if (host) return host;
      host = document.createElement('div');
      host.id = 'aa-panel-host';
      if (container && container.parentNode) {
        container.insertAdjacentElement('afterend', host);
      } else {
        document.body.appendChild(host);
      }
      return host;
    },

    // AM-16256: keep an accessible name on the field. Live-filter mode: strip
    // the combobox metadata the library adds so this stays a plain search box.
    enhanceInput() {
      const input = this.getInput();
      if (!input) return;

      if (!input.getAttribute('aria-label')) {
        input.setAttribute('aria-label', algolia.translations.searchForProduct);
      }
      [
        'aria-autocomplete',
        'aria-haspopup',
        'aria-expanded',
        'aria-controls',
        'aria-owns',
        'aria-activedescendant',
        'aria-describedby',
      ].forEach((attr) => input.removeAttribute(attr));
      if (input.getAttribute('role') === 'combobox') {
        input.setAttribute('role', 'searchbox');
      }
    },

    // AM-16255: announce the number of results, re-announcing on every new
    // search. Clearing first forces re-announcement even for identical counts.
    announce(count) {
      const live = document.getElementById(this.liveId);
      if (!live) return;
      const message = !count
        ? algolia.translations.noResultFound
        : count === 1
        ? algolia.translations.oneResultFound
        : count + ' ' + algolia.translations.resultsFound;
      live.textContent = '';
      clearTimeout(this.announceTimer);
      this.announceTimer = setTimeout(() => {
        live.textContent = message;
      }, 150);
    },

    // Live-filter semantics: expose results as a labelled region and strip the
    // listbox/option roles so each card's inner controls stay fully navigable.
    enhancePanel(root) {
      if (!root) return;
      const input = this.getInput();
      if (input) input.removeAttribute('aria-activedescendant');

      const panel = root.closest('.aa-Panel') || root;
      panel.setAttribute('role', 'region');
      panel.setAttribute(
        'aria-label',
        algolia.translations.searchResults || 'Search results'
      );

      root.querySelectorAll('.aa-Source').forEach((source) => {
        const title = source.querySelector('.aa-SourceHeaderTitle');
        if (title) {
          if (!title.id) title.id = 'aa-src-' + this.seq++;
          source.setAttribute('role', 'group');
          source.setAttribute('aria-labelledby', title.id);
        }

        // The products source also renders empty placeholder list items (the
        // real cards live in the Vue list). Hide those empties from AT.
        const vueList = source.querySelector('#autocomplete-vue-list');
        if (vueList) {
          source.querySelectorAll('.aa-List').forEach((list) => {
            if (list !== vueList) list.setAttribute('aria-hidden', 'true');
          });
        }
      });

      // Remove combobox/listbox semantics the library applies on each render.
      root
        .querySelectorAll('[role="listbox"]')
        .forEach((el) => el.removeAttribute('role'));
      root.querySelectorAll('[role="option"]').forEach((el) => {
        el.removeAttribute('role');
        el.removeAttribute('aria-selected');
      });

      // AM-16257: re-anchor the panel below the input bar (see positionPanel).
      this.positionPanel();
    },

    // AM-16257: the panel now lives inside the positioned header, so anchor it
    // with fixed positioning directly below the search input bar (full width),
    // overriding the library's page-coordinate inline styles. Fixed positioning
    // is viewport-relative, so it is unaffected by the header's own context.
    // Only apply on desktop (>= 1024px) to avoid breaking mobile layout.
    positionPanel() {
      const panel = document.querySelector('.aa-Panel');
      if (!panel || !container) return;
      
      // Only apply fixed positioning on desktop
      if (window.innerWidth >= 1024) {
        const bar = container.getBoundingClientRect();
        panel.style.setProperty('position', 'fixed', 'important');
        panel.style.setProperty('top', Math.round(bar.bottom) + 'px', 'important');
        panel.style.setProperty('left', '0', 'important');
        panel.style.setProperty('right', 'auto', 'important');
        panel.style.setProperty('width', '100vw', 'important');
        panel.style.setProperty('max-width', '100vw', 'important');
      } else {
        // Remove inline styles on mobile to let CSS handle positioning
        panel.style.removeProperty('position');
        panel.style.removeProperty('top');
        panel.style.removeProperty('left');
        panel.style.removeProperty('right');
        panel.style.removeProperty('width');
        panel.style.removeProperty('max-width');
      }
    },

    // Is the given element within the search input or the results panel?
    inScope(el) {
      if (!el) return false;
      const host = document.getElementById('aa-panel-host');
      return (
        (container && container.contains(el)) || (host && host.contains(el))
      );
    },

    // First keyboard-focusable element inside the results panel.
    firstFocusable() {
      const host = document.getElementById('aa-panel-host');
      return host
        ? host.querySelector(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        : null;
    },

    // AM-16339: make the results reachable and operable with the keyboard.
    // Autocomplete.js keeps focus on the input (combobox virtual focus) and
    // closes the panel on blur, so keyboard and screen reader users could not
    // Tab into the results. Here we move focus from the input into the first
    // result on Tab, keep the panel open while focus stays within the input or
    // results, return to the input on Shift+Tab from the first result, and
    // close on Escape or when focus leaves the search. Arrow keys are blocked
    // (a live filter has no aria-activedescendant roving).
    manageFocus() {
      if (this.keysBound || !container) return;
      this.keysBound = true;
      const self = this;

      // Keys while the search input is focused.
      container.addEventListener(
        'keydown',
        (event) => {
          if (event.target !== self.getInput()) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.stopImmediatePropagation();
            return;
          }
          if (event.key === 'Tab' && !event.shiftKey) {
            const first = self.firstFocusable();
            if (first) {
              // Move focus straight into the results (not the next header
              // control) and keep the panel open.
              event.preventDefault();
              event.stopImmediatePropagation();
              first.focus();
            }
          }
        },
        true
      );

      // Keys while focus is within the results panel.
      const host = this.getPanelHost();
      host.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          if (window.autocompleteGlobal) {
            window.autocompleteGlobal.setIsOpen(false);
          }
          const input = self.getInput();
          if (input) input.focus();
          return;
        }
        if (
          event.key === 'Tab' &&
          event.shiftKey &&
          document.activeElement === self.firstFocusable()
        ) {
          // Shift+Tab from the first result returns to the search input.
          event.preventDefault();
          const input = self.getInput();
          if (input) input.focus();
        }
      });

      // Keep the panel open while focus stays within the input or results
      // (stops the library closing it when focus moves into the results).
      const keepOpen = (event) => {
        if (self.inScope(event.target) && self.inScope(event.relatedTarget)) {
          event.stopImmediatePropagation();
        }
      };
      document.addEventListener('blur', keepOpen, true);
      document.addEventListener('focusout', keepOpen, true);

      // Close the panel once focus leaves the search entirely.
      document.addEventListener(
        'focusout',
        (event) => {
          if (!self.inScope(event.target)) return;
          setTimeout(() => {
            if (
              !self.inScope(document.activeElement) &&
              window.autocompleteGlobal
            ) {
              window.autocompleteGlobal.setIsOpen(false);
            }
          }, 0);
        },
        true
      );

      // AM-16257: keep the panel anchored below the input bar on resize.
      window.addEventListener('resize', () => self.positionPanel());
    },
  };

  const autocompleteGlobal = autocomplete({
    container,
    placeholder: 'search here',
    openOnFocus: true,
    debug: algolia.config.autocomplete_debug,
    plugins,
    // AM-16257: render the results panel right after the header (see
    // getPanelHost) instead of at the end of the page body, for reading order.
    panelContainer: algolia.a11y.getPanelHost(),
    render({ elements, render, html, state }, root) {
      window.localStorage.setItem('algolia-result', JSON.stringify(state));
      const sourceIdsToExclude = ['products'];
      const [querySuggestionsPluginResults] = state.collections.filter(
        ({ source }) => source.sourceId === 'querySuggestionsPlugin'
      );
      const displaySuggestions =
        algolia.config.autocomplete_query_suggestions &&
        querySuggestionsPluginResults &&
        querySuggestionsPluginResults.items.length > 0;
      const hasLeftPanelResults =
        state.collections
          .filter(
            ({ source }) => sourceIdsToExclude.indexOf(source.sourceId) === -1
          )
          .reduce((prev, curr) => prev + curr.items.length, 0) > 0 || displaySuggestions;
      render(
        hasLeftPanelResults
          ? algolia.mainTemplate({ html, state }, elements, displaySuggestions)
          : algolia.mainProductsTemplate({ html, state }, elements),
        root
      );

      // Accessibility (live filter): expose results as a navigable region and
      // announce the ACTUAL product match count (AM-16255) — the same number as
      // the "products matching (N)" heading — not the capped count of rendered
      // items across sources (which was roughly constant regardless of query).
      algolia.a11y.enhanceInput();
      algolia.a11y.enhancePanel(root);
      const productCount =
        state.context && typeof state.context.nbProducts === 'number'
          ? state.context.nbProducts
          : state.collections.reduce(
              (total, group) => total + group.items.length,
              0
            );
      algolia.a11y.announce(productCount);
      liveFilter.apply(root, countResults(state));
    },
    renderNoResults({ render, html, state }, root) {
      render(algolia.noResultsTemplate({ html, state }), root);
      addInfoNoResult();

      // Accessibility: keep the input wired up and announce "no results".
      algolia.a11y.enhanceInput();
      algolia.a11y.announce(0);
      liveFilter.apply(root, 0);
    },
    onSubmit({ state }) {
      if (window.latestRedirectUrl) {
        window.location.href = window.latestRedirectUrl;
      } else {
        window.location.href = `/search?q=${encodeURIComponent(state.query.trim())}`;
      }
    },
  });
  // Accessibility: create the SR-only live region, prime the input, and set up
  // keyboard access to the results (AM-16339).
  algolia.a11y.ensureSrNodes();
  algolia.a11y.enhanceInput();
  algolia.a11y.manageFocus();

  window.autocompleteGlobal = autocompleteGlobal;
  liveFilter.observe();
})(window.algoliaShopify);
